import errorMonitoring from "lib/errorMonitoring";
import { omit } from "lib/lodash";
import formatTableDataForV1 from "./formatTableDataForV1";
import formatTextboxDataForV1 from "./formatTextboxDataForV1";
import formatImageDataForV1 from "./formatImageDataForV1";

const reportOrphanIds = orphanIds => {
  try {
    errorMonitoring.sendError(
      new Error("formatDataForV1: orphan element ids in page.elementsOrder"),
      span => {
        span.setAction("formatDataForV1.orphanIds");
        span.setTags({
          orphanCount: String(orphanIds.length),
          orphanIds: orphanIds.slice(0, 10).join(",")
        });
      }
    );
  } catch {
    /* telemetry must never break the save */
  }
};

/* Drops ids whose lookup in data.elements is undefined and reports them.
 * page.elementsOrder is expected to be a subset of keys(data.elements);
 * when an upstream reducer breaks that invariant we filter the orphan
 * out so the save still succeeds and report it to AppSignal so the
 * upstream source remains visible. */
const resolveElementsForIds = (elementsIds, data) => {
  const orphanIds = [];
  const elements = [];

  elementsIds.forEach(id => {
    const element = data.elements[id];
    if (element === undefined) {
      orphanIds.push(id);
      return;
    }
    elements.push(element);
  });

  if (orphanIds.length > 0) {
    reportOrphanIds(orphanIds);
  }

  return elements;
};

const groupData = ({ elements, data }) => {
  const groupElements = elements.filter(element => element.type === "group");

  return groupElements.map(group => {
    return {
      id: group.uniqueId,
      name: group.name,
      /* Mirror the orphan-id filter elementsData applies so the v1
       * payload's groups[].elements never references an id absent from
       * elements[]. Reporting stays with elementsData. */
      elements: group.elementsOrder.filter(
        id => data.elements[id] !== undefined
      ),
      visible: !group.isHidden,
      opacity: group.opacity,
      angle: group.angle
    };
  });
};

const elementsData = ({ elements, data }) => {
  const elementsList = [];

  elements.forEach(element => {
    if (element.type === "group") {
      const nestedElements = resolveElementsForIds(element.elementsOrder, data);
      nestedElements.forEach(nestedElement => {
        elementsList.push({
          ...formatElementForV1(nestedElement),
          groupId: element.uniqueId
        });
      });
    } else {
      elementsList.push(formatElementForV1(element));
    }
  });

  return elementsList.map(element => omit(element, ["getElement"]));
};

const formatElementForV1 = element => {
  let elementFormatted = null;

  switch (element.type) {
    case "table":
      elementFormatted = formatTableDataForV1(element);
      break;

    case "textbox":
      elementFormatted = formatTextboxDataForV1(element);
      break;

    case "grid":
    case "vector":
      elementFormatted = formatImageDataForV1(element);
      break;

    case "chart":
      elementFormatted = element;
      break;

    default:
      elementFormatted = element;
  }

  return elementFormatted;
};

const serializeBackgroundColor = backgroundColor => {
  if (
    typeof backgroundColor === "object" &&
    backgroundColor !== null &&
    backgroundColor.type &&
    backgroundColor.stops
  ) {
    return "gradient:" + JSON.stringify(backgroundColor);
  }
  return backgroundColor;
};

const pageDataById = ({
  id,
  data,
  documentSmartText,
  documentSmartTextOrder
}) => {
  const page = data.pages[id];
  const elements = resolveElementsForIds(page.elementsOrder, data);

  return {
    width: data.width,
    height: data.height,
    elements: elementsData({ elements, data }),
    backgroundColor: serializeBackgroundColor(page.backgroundColor),
    groups: groupData({ elements, data }),
    uniqueId: id,
    guides: page.guides,
    duration: page.duration,
    isDurationManual: page.isDurationManual,
    animatedElements: page.animatedElements || {},
    ...(page.quickInputOrder && { quickInputOrder: page.quickInputOrder }),
    openQuickInput: page.openQuickInput || false,
    pageName: page.pageName,
    ...(page.outgoingTransition && {
      outgoingTransition: page.outgoingTransition
    }),
    /* Piggyback persistence: asset-service has no column for
     * documentSmartText but `data` is an opaque JSON blob (same trick as
     * animatedElements). Stamp the design-wide collection onto every
     * page; the loader hoists it back to the top level. */
    ...(documentSmartText && { documentSmartText }),
    ...(documentSmartTextOrder && { documentSmartTextOrder })
  };
};

const pagesData = data => {
  const documentSmartText =
    data.documentSmartText && Object.keys(data.documentSmartText).length > 0
      ? data.documentSmartText
      : undefined;
  const documentSmartTextOrder =
    Array.isArray(data.documentSmartTextOrder) &&
    data.documentSmartTextOrder.length > 0
      ? data.documentSmartTextOrder
      : undefined;
  return data.pagesOrder.map(pageId =>
    pageDataById({
      id: pageId,
      data,
      documentSmartText,
      documentSmartTextOrder
    })
  );
};

const formatDataForV1 = data => {
  return {
    bleed: data.bleed,
    createdAt: data.createdAt,
    data: pagesData(data),
    designId: data.designId,
    documentRestrictions: data.restrictions,
    id: data.id,
    ordered: data.ordered,
    updatedAt: data.updatedAt
  };
};

export default formatDataForV1;
